import { Temporal } from '@js-temporal/polyfill';
import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { DTB } from './databin';
import { KPAR } from './keyword$';
import { STOREPARAMS } from './manifest$';
import { SGPAR } from './style-group';
import { CTIPAR } from './table$';
import { TableInstance } from './table-instance';
import { VPAR } from './view$';

import { ProjectTeam } from './project-team';
import { Subject, debounceTime } from 'rxjs';
import { ScriptProject } from './script-project';
import { ADOPT_DATA, ADOPT_PARAMS, AutoUser } from './auto-user';
import { ScriptAutoUser } from './script-auto-user';
import { Comment } from './comment';
import { ProjectInList } from './project-in-list';

export type PPAR = {
    id?: string;
    meta?: Object;
    databins?: {
        xitemLs: Object[];
        databinLs: DTB[];
    };
    commentLs: any[];
    pinLs: any[];
    styleLs?: SGPAR[];
    keywords?: KPAR;
    viewLs?: VPAR[];
    binaryTreeLs: any[];
    binaryStorageLs: any[];
    datasetLs?: { xitemLs: any[] }[];
};

const PROJECT_CLOUD_FIELDS = ['title', 'tagLs'];
export class Project {
    public id: string;
    private _isViewMode = false;
    private _isEditMode = false;

    // isLocal2Cloud = false

    private _updatedValuesSet = new Set<string>();

    private _detailTBLI?: TableInstance;
    private _reportTBLI?: TableInstance;

    private _team?: ProjectTeam;

    private _valueMap = new Map<string, any>();
    // private _teamMap = new Map<string, ProjectMembership>()

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    constructor(
        private coords: CoordinatorService,
        params: any,
    ) {
        params = params || {};
        this.id = params.id || this.coords.uniqueId();

        this.isViewMode = params.isViewMode || false;

        Object.keys(params).forEach((k) => {
            if (k === 'id' || k === 'isNew') return;
            let v = params[k];
            if (k.endsWith('Date')) {
                try {
                    if (!v) return;
                    v = Temporal.Instant.from(v);
                } catch (e) {
                    // this.coords.utils$.consoleError("PROJECT DATETIME IN WRONG FORMAT", e)
                }
            }
            this._valueMap.set(k, v);
        });

        this._markForManifestStore$.subscribe(() => this._manifestStore());

        this._generateBrowserMeta();
    }

    private _generateBrowserMeta() {
        this.coords.browserTitle = this.title;
    }

    clearScriptCurrentUser(arg: string) {
        switch (arg) {
            case 'all':
                this.coords.comment$.clearCurrentUser();
                this.coords.binaryTree$.clearValuesCurrentUser(arg);
                break;
            case 'comments':
                this.coords.comment$.clearCurrentUser();
                break;
            default:
                this.coords.binaryTree$.clearValuesCurrentUser(arg);
        }
        this.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    // private _importViewerAsAutouser(ls: BinaryTree[]) {
    //     console.log('importViewerAsAutouser', ls);
    //     const script = `
    //          const au = $autouserManager.get("Autouser", true);

    //     `;
    //     this.coords.script$.run(script);
    // }

    async importViewer() {
        this._isViewMode = false;
        this.coords.stage$.set('project');

        // autouserLs?.length && this._importViewerAsAutouser(autouserLs);

        const pil = new ProjectInList(this.coords, {
            id: this.id,
            title: this.title,
            isCloud: false,
            lastUpdate: Temporal.Now.instant().toString(),
        });
        this.coords.project$.setProjectInList(pil);
        pil && pil.localOpenUpdate();
        this._manifestStore();
        this.coords.dataset$.main.markForManifestStore();
        this.coords.view$.markForManifestStore();
        this.coords.keyword$.markForManifestStore();
        this.coords.comment$.markForManifestStore();
        this.coords.attachment$.markForManifestStore();
        this.coords.display$.markForCheck();
        this.coords.autouser$.markForManifestStore();
        this.coords.display$.closeDialog();
    }

    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }

    exportJsonAllTeam() {
        return this.team?.memberLs.map((x) => ({
            id: x.id,
            name: x.user?.displayName,
            role: x.role,
        }));
    }

    public get isViewMode() {
        const lastHubPill = this.coords.project$.lastHubPill;
        if (lastHubPill?.id === this.id && lastHubPill.openAs === 'viewer') return true;
        return this._isViewMode;
    }

    public set isViewMode(value) {
        this._isViewMode = value;
    }

    public get isEditMode() {
        return this._isEditMode;
    }

    enableEditMode() {
        this._isEditMode = true;
    }

    cancelEditMode() {
        this._isEditMode = false;
    }

    loadingProgress() {
        if (this.isCloud) {
            for (let cf of this.team!.cloudFileLs) {
                if (cf.isDownloading) {
                    const s = this.coords.utils$.bytesToHumanReadable(cf.size);
                    return { text: `${this.coords.text('Downloading')} ${s}` };
                }
            }
        }
        return { text: this.coords.text('Processing') };
    }

    uploadingProgress() {
        if (!this.team) return { text: '' };
        for (let uf of this.coords.upload$.activeLs) {
            const s = this.coords.utils$.bytesToHumanReadable(uf.size);
            return { text: `${this.coords.text('Uploading')} ${s}` };
        }

        return { text: '' };
    }

    async terminate() {
        await this._team?.terminate();
        await this.coords.user$.closeQuerySubscription('projectUserDetails');
    }

    get team() {
        return this._team;
    }

    set team(value: ProjectTeam | undefined) {
        this._team = value;
    }

    private _manifestStore() {
        if (!this.title?.trim()) return;
        this.coords.manifest$.store(this.manifestParams);
    }

    get manifestParams(): STOREPARAMS {
        return {
            id: this.id,
            type: 'project',
            valueMap: this._valueMap,
        };
    }

    adoptData(arg: ADOPT_DATA, params: ADOPT_PARAMS = {}) {
        if (Array.isArray(arg)) {
            const uId = this.coords.user$.currentId;
            arg.forEach((origC: Comment) => {
                const c = new Comment(this.coords, {
                    text: origC.text,
                    originId: origC.originId,
                    ownerId: uId,
                });
                this.coords.comment$.addComment(c);
            });
        } else {
            this.coords.binaryTree$.adoptData(arg, params);
        }
    }

    json() {
        return this.coords.utils$.toJSONready(this._valueMap);
    }

    get isCloud() {
        const pil = this.coords.project$.getProjectInList(this.id);
        if (pil?.isHub) return false;
        return !!pil?.isCloud;
    }

    get isHub() {
        return !!this.coords.project$.getProjectInList(this.id)?.isHub;
    }

    dialogInvitation() {
        this.coords.display$.dialog$.next({
            action: 'team-invitation',
        });
    }
    dialogCodeGenerate() {
        this.coords.display$.dialog$.next({
            action: 'team-generate',
        });
    }
    dialogChangeRole(element: any) {
        this.coords.display$.dialog$.next({
            action: 'team-change-role',
            element: element,
        });
    }

    tbli(isReport = false) {
        if (!isReport && this._detailTBLI) {
            this._detailTBLI.isEditable = this.coords.acl$.isOn('project', 'edit');
            return this._detailTBLI;
        }
        if (isReport && this._reportTBLI) return this._reportTBLI;

        const params: CTIPAR = {
            origin: this,
            templateId: 'project-detail',
        };

        if (isReport) {
            params.templateModification = {
                id: 'report',
                fun: (templ) => {
                    templ.hideEmpty = true;
                    templ.isGlobalEdit = false;
                    // templ.pinId = "inProjectSummary"
                    return templ;
                },
            };
        }
        const inst = this.coords.table$.createTableInstance(params);
        inst.isEditable = this.coords.acl$.isOn('project', 'edit');

        if (isReport) this._reportTBLI = inst;
        else this._detailTBLI = inst;

        if (!this.title) inst.isEditOn = true;
        return inst;
    }

    get title() {
        return this._valueMap.get('title');
    }

    get(tag: string) {
        if (tag == 'id') return this.id;
        return this._valueMap.get(tag);
    }

    set(tag: string, val: any) {
        if (this.get(tag) === val || tag === 'id') return;
        if (tag === 'title' && !val) return;
        this._valueMap.set(tag, val);
        this._updatedValuesSet.add(tag);
    }

    get valueMap() {
        return new Map(this._valueMap);
    }

    get shareLink() {
        const k = this.coords.uniqueId();
        const key = `/r/${k}`;
        const path = `/hub/share/${k}`;
        const url = new URL(
            key,
            `${window.location.protocol}//${window.location.hostname}`,
        ).toString();
        // url.searchParams.set('pil', this.id);
        return { key, path, url };
    }

    get link() {
        const url = new URL(
            `/pil/${this.id}`,
            `${window.location.protocol}//${window.location.hostname}`,
        );
        // url.searchParams.set('pil', this.id);
        return url.toString();
    }

    async updateCloudInfo() {
        try {
            const cloudData: { [tag: string]: any } = {};
            PROJECT_CLOUD_FIELDS.forEach((x) => {
                let v = this.get(x);
                if (!v && x.endsWith('Ls')) v = [];
                cloudData[x] = v;
            });

            const resp = await this.coords.firebase$.firestore.addDocument('projects', this.id, {
                projectId: this.id,
                data: cloudData,
            });
        } catch (e) {
            console.error('UPDATE CLOUD PROJECT INFO', e);
        }
    }

    async afterConfirmChanges() {
        this.markForManifestStore();
        const pil = this.coords.project$.getProjectInList(this.id)!;
        if (this._updatedValuesSet.has('title')) pil.title = this.title;

        if (this.isCloud) {
            for (const tag of PROJECT_CLOUD_FIELDS) {
                if (this._updatedValuesSet.has(tag)) {
                    await this.updateCloudInfo();
                    break;
                }
            }
        }
        this._updatedValuesSet.clear();
        this.coords.report$.clear();
    }

    reportValue(tag: string) {
        return tag;
    }

    reportData() {
        return this.tbli(true);
    }

    get scriptClass() {
        return new ScriptProject(this.coords, this);
    }

    get isFirstRun() {
        return !!this.coords.project$.getProjectInList(this.id)?.isFirstRun;
    }
}
